// Studio launch's active-work deadline. The desktop process and standalone
// browser use the same clock; Root only reports stages. It remains alive across
// route unmounts and carries consumed time through automatic replacement.
// Human-action surfaces explicitly hold/dispose waits; machine stages cannot
// extend the deadline by reporting progress. One terminal retires pending work.
//
// Per rule: reference-rules-in-comments (ai.knowledge/global).
// Per rule: never-use-type-assertions (ai.knowledge/global) — stages are a
// typed union, not bare strings scattered through the boot flow.

/**
 * Renderer-reported checkpoints within the process/browser-owned launch.
 * Main can also report its entry/server/window stages. The last accepted stage
 * identifies where an attempt timed out; reporting progress never resets time.
 */
export const BOOT_STAGE_VALUES = [
  'started',
  'awaiting-linked-agent',
  'resolving-linked-room',
  'listing-rooms',
  'awaiting-preferences',
  'resolving-auto-link',
  'awaiting-auto-link-settle',
  'room-sync-poll',
  'resolving-room',
  'loading-organizations',
  'loading-members',
  'navigating'
] as const;

export type BootStage = (typeof BOOT_STAGE_VALUES)[number];

// Conservative, Product-confirmable proposal: 307 seconds of active machine
// work across process entry, server/window startup, account bootstrap and Studio
// readiness. Explicit human waits are excluded. This is not a measured healthy
// cold-boot SLA or a guarantee that every bounded operation can spend its maximum.
// Allocation constants retain a single named tunable total for both owners.
export const AUTO_LINK_SETTLE_TIMEOUT_MS = 180_000;
export const AUTO_LINK_ROOM_SYNC_TIMEOUT_MS = 10_000;
export const BOOT_AWAITED_CALL_BUDGET_MS = 57_000;
export const BOOT_DEADLINE_MARGIN_MS = 60_000;
export const BOOT_DEADLINE_MS =
  AUTO_LINK_SETTLE_TIMEOUT_MS +
  AUTO_LINK_ROOM_SYNC_TIMEOUT_MS +
  BOOT_AWAITED_CALL_BUDGET_MS +
  BOOT_DEADLINE_MARGIN_MS;

/**
 * Resolve an E2E-only override for renderer callers. Desktop's authoritative
 * owner reads the process environment at main entry. Production uses the shared
 * 307-second budget; a shortened test must still prove it reached its fault stage.
 */
export function resolveBootDeadlineMs(): number {
  const env = (
    globalThis as {
      window?: { electron?: { process?: { env?: Record<string, string> } } };
    }
  ).window?.electron?.process?.env;

  if (!env || env.IRIS_E2E_TEST !== '1') {
    return BOOT_DEADLINE_MS;
  }

  const raw = env.IRIS_E2E_BOOT_DEADLINE_MS;
  if (typeof raw !== 'string') {
    return BOOT_DEADLINE_MS;
  }

  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : BOOT_DEADLINE_MS;
}

/**
 * The stage at which an e2e run should force the boot generator to HANG
 * (await a never-resolving promise), or null in production / when unset. Gated
 * on IRIS_E2E_TEST=1 so it can never engage in a real boot. This is the
 * hardware-free fault injection the CI receipt uses to prove the watchdog fires
 * boot_failed(boot_timeout). Desktop main emits the terminal when its active
 * budget expires; reaching this checkpoint is a separate prerequisite.
 */
export function resolveE2EBootHangStage(): BootStage | null {
  const env = (
    globalThis as {
      window?: { electron?: { process?: { env?: Record<string, string> } } };
    }
  ).window?.electron?.process?.env;

  if (!env || env.IRIS_E2E_TEST !== '1') {
    return null;
  }

  const raw = env.IRIS_E2E_BOOT_HANG_STAGE;
  return isBootStage(raw) ? raw : null;
}

// Set<string> (not Set<BootStage>) so `has(value: string)` accepts the unknown
// input directly — the membership check itself is what narrows the type.
const BOOT_STAGE_SET = new Set<string>(BOOT_STAGE_VALUES);

/** Type guard so an env string narrows to BootStage without a type assertion. */
export function isBootStage(value: unknown): value is BootStage {
  return typeof value === 'string' && BOOT_STAGE_SET.has(value);
}

/** One active-work clock for Studio launch, shared by desktop and browser owners. */
export const BOOT_BUDGET_MS = BOOT_DEADLINE_MS;
/**
 * Absolute sanity bound for a snapshot's `remainingMs`, and the most a launch
 * budget can be lengthened to. Deliberately NOT the production default: the
 * constructor stopped clamping `budgetMs` to BOOT_BUDGET_MS so an e2e project
 * can lengthen its launch clock (see the `budgetMs` doc below), but
 * `isBootBudgetSnapshot` kept validating against BOOT_BUDGET_MS — so main could
 * install a budget the renderer then REFUSED. The preload's attach reply
 * carried a perfectly good snapshot, `initializeBootBudget` took its
 * invalid-snapshot early return, and that document ran with no boot budget at
 * all: `finishBoot` false forever, no boot_succeeded, a launch that never
 * settled from the renderer, and every health pong unanswerable. Both sides now
 * agree on ONE bound by construction: the constructor and `restart()` clamp to
 * it, and the validator checks it. 24h is the largest override the tree uses
 * (create-studio-outcome-notice.spec.ts) and a value no real launch can reach.
 * Per rule: settle-every-terminal-branch (ai.knowledge/global) — a budget the
 * owner installs must be one its consumers accept, or the terminal never fires.
 */
export const BOOT_BUDGET_CEILING_MS = 24 * 60 * 60 * 1000;
/** The budget a launch actually gets: the override when supplied, never above the ceiling. */
export const boundedBudgetMs = (budgetMs: number | undefined): number =>
  budgetMs === undefined ? BOOT_BUDGET_MS : Math.min(budgetMs, BOOT_BUDGET_CEILING_MS);
export type BootOutcome = 'succeeded' | 'failed' | 'cancelled' | null;
export interface BootBudgetSnapshot {
  attemptId: string;
  accountId: string | null;
  generation: number;
  startedAt: number;
  updatedAt: number;
  activeMs: number;
  excludedMs: number;
  remainingMs: number;
  outcome: BootOutcome;
  reason: string | null;
  stage: string;
}

/** Persisted data is untrusted, including browser session storage and relaunch input. */
export function isBootBudgetSnapshot(value: unknown): value is BootBudgetSnapshot {
  if (!value || typeof value !== 'object') return false;
  const s = value as BootBudgetSnapshot;
  return (
    typeof s.attemptId === 'string' &&
    s.attemptId.length > 0 &&
    s.attemptId.length < 128 &&
    (s.accountId === null || typeof s.accountId === 'string') &&
    [s.generation, s.startedAt, s.updatedAt, s.activeMs, s.excludedMs, s.remainingMs].every(
      (n) => Number.isFinite(n) && n >= 0
    ) &&
    // The ceiling, not BOOT_BUDGET_MS: a lengthened e2e budget is a legal
    // snapshot (see BOOT_BUDGET_CEILING_MS). Rejecting it here is how a renderer
    // ends up with no budget while main's clock is healthy.
    s.remainingMs <= BOOT_BUDGET_CEILING_MS &&
    [null, 'succeeded', 'failed', 'cancelled'].includes(s.outcome) &&
    (s.reason === null || typeof s.reason === 'string') &&
    typeof s.stage === 'string'
  );
}

export class BootBudget {
  private state: BootBudgetSnapshot;
  private waits = new Set<string>();
  private timer: ReturnType<typeof setTimeout> | undefined;
  // Set only by expire(), for the one charge that records a deadline stamped ahead of now.
  private holdUpdatedAt = false;
  private controller = new AbortController();
  private listeners = new Set<() => void>();

  constructor(
    private options: {
      attemptId: string;
      restored?: unknown;
      now?: () => number;
      /**
       * This launch's budget. Defaults to BOOT_BUDGET_MS, which is what every
       * production path gets: the only caller in the app that passes a value is
       * main's owner (electron/src/main/boot-budget.ts), and it only does so
       * behind `IRIS_E2E_TEST === '1'`. The renderer never passes one.
       *
       * It used to be clamped with `Math.min(budgetMs, BOOT_BUDGET_MS)`, which
       * made `IRIS_E2E_BOOT_DEADLINE_MS` a shorten-only seam: main accepted a
       * longer e2e budget, handed it here, and it was silently discarded. That
       * cost a day of CI archaeology on ENG-4720 -- an own-launch project whose
       * five tests share ONE launch ran past the 307s production budget, and the
       * override written to stop it did nothing. The clamp was never a boundary
       * either: the `restored` branch below copies `remainingMs` verbatim and
       * has always bypassed it.
       * Per rule: reference-rules-in-comments (ai.knowledge/global).
       */
      budgetMs?: number;
      onTerminal?: (snapshot: BootBudgetSnapshot) => void;
    }
  ) {
    const now = this.now();
    this.state = isBootBudgetSnapshot(options.restored)
      ? { ...options.restored }
      : {
          attemptId: options.attemptId,
          accountId: null,
          generation: 0,
          startedAt: now,
          updatedAt: now,
          activeMs: 0,
          excludedMs: 0,
          remainingMs: boundedBudgetMs(options.budgetMs),
          outcome: null,
          reason: null,
          stage: 'application-entry'
        };
    // No rendered human wait survives document/process replacement. Its absence
    // is machine time, so automatic recovery cannot buy another full budget.
    this.charge();
    if (this.state.outcome !== null) this.controller.abort();
    this.schedule();
  }

  private now(): number {
    return (this.options.now ?? Date.now)();
  }
  private charge(): void {
    // A settled attempt freezes its clock. boot_duration_ms is
    // `updatedAt - startedAt`, so advancing the timestamp after a terminal made
    // the reported duration keep growing for as long as anything still charged
    // the budget (observed: a failure at 100ms reading 600ms after another
    // 500ms). The terminal guard must come BEFORE the timestamp, not after.
    if (this.state.outcome !== null) return;
    const now = this.now();
    const elapsed = Math.max(0, now - this.state.updatedAt);
    // A backward step must snap to now. Holding the old stamp freezes the
    // countdown, and the next schedule() starts the remaining time over.
    // expire() is the exception: it stamps the deadline ahead of the clock, and
    // finish() charges once to keep that stamp.
    if (this.holdUpdatedAt) {
      this.state.updatedAt = Math.max(this.state.updatedAt, now);
      this.holdUpdatedAt = false;
    } else {
      this.state.updatedAt = now;
    }
    if (this.waits.size) this.state.excludedMs += elapsed;
    else {
      this.state.activeMs += elapsed;
      this.state.remainingMs = Math.max(0, this.state.remainingMs - elapsed);
    }
  }
  private schedule(): void {
    clearTimeout(this.timer);
    if (this.state.outcome !== null || this.waits.size) return;
    this.timer = setTimeout(() => this.expire(), this.state.remainingMs);
    // Main's clock must not keep a quit process alive.
    (this.timer as unknown as { unref?: () => void }).unref?.();
  }
  /**
   * The deadline timer firing is the budget running out. The timer runs on the
   * event loop's clock and `charge()` reads `now()`, and the two can disagree by
   * a millisecond (Windows CI reported boot_active_ms 306,999 for a 307,000
   * budget). Charge any remainder so the terminal reports the budget spent.
   */
  private expire(): void {
    this.charge();
    if (this.state.outcome !== null || this.waits.size) return;
    const remainder = this.state.remainingMs;
    this.state.activeMs += remainder;
    this.state.updatedAt += remainder;
    this.state.remainingMs = 0;
    this.holdUpdatedAt = true;
    this.finish('failed', 'boot_timeout');
  }
  private changed(): void {
    for (const listener of this.listeners) {
      try {
        listener();
      } catch {
        /* observers cannot prevent settlement */
      }
    }
  }
  snapshot(): BootBudgetSnapshot {
    this.charge();
    return { ...this.state };
  }
  get signal(): AbortSignal {
    return this.controller.signal;
  }
  get awaitingHuman(): boolean {
    return this.waits.size > 0;
  }
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  stage(stage: string): void {
    this.state.stage = stage;
  }
  humanWait(key: string, waiting: boolean): void {
    this.charge();
    if (this.state.outcome !== null) return;
    if (this.state.remainingMs === 0) {
      this.finish('failed', 'boot_timeout');
      return;
    }
    if (waiting) this.waits.add(key);
    else this.waits.delete(key);
    this.schedule();
    this.changed();
  }
  releaseHumanWaits(): void {
    this.charge();
    this.waits.clear();
    this.schedule();
    this.changed();
  }
  bindAccount(accountId: string | null, nextAttemptId: string): void {
    if (this.state.accountId === accountId) return;
    if (this.state.accountId !== null) this.restart(nextAttemptId);
    this.state.accountId = accountId;
    this.changed();
  }
  restart(attemptId: string): void {
    // Retire captured async writers before replacing identity. Cancellation is
    // not a success/failure event (settle-every-terminal-branch, global).
    this.controller.abort();
    this.controller = new AbortController();
    const now = this.now();
    this.state = {
      attemptId,
      accountId: this.state.accountId,
      generation: this.state.generation + 1,
      startedAt: now,
      updatedAt: now,
      activeMs: 0,
      excludedMs: 0,
      remainingMs: boundedBudgetMs(this.options.budgetMs),
      outcome: null,
      reason: null,
      stage: 'application-entry'
    };
    this.holdUpdatedAt = false;
    this.waits.clear();
    this.schedule();
    this.changed();
  }
  finish(outcome: Exclude<BootOutcome, null>, reason: string | null = null): boolean {
    const requested = outcome;
    this.charge();
    if (this.state.outcome !== null) return false;
    // A late ready may not beat a deadline merely because the event loop was busy.
    if (outcome === 'succeeded' && this.state.remainingMs === 0) {
      outcome = 'failed';
      reason = 'boot_timeout';
    }
    this.state.outcome = outcome;
    this.state.reason = reason;
    clearTimeout(this.timer);
    this.controller.abort();
    this.changed();
    // Telemetry/UI failures must never strand state or waiters.
    try {
      this.options.onTerminal?.(this.snapshot());
    } catch {
      /* settled above */
    }
    return this.state.outcome === requested;
  }
  async during<T>(work: Promise<T>): Promise<T> {
    const signal = this.signal;
    if (signal.aborted) throw new Error('Boot attempt ended');
    let abort!: () => void;
    const ended = new Promise<never>((_, reject) => {
      abort = () => reject(new Error('Boot attempt ended'));
      signal.addEventListener('abort', abort, { once: true });
    });
    try {
      return await Promise.race([work, ended]);
    } finally {
      signal.removeEventListener('abort', abort);
    }
  }
}
